import React, { createContext, useContext, useState, useCallback, useMemo } from "react";

import { ParkingSession } from "@/utils/api/ParkingSessionService";

// ============================================
// Types
// ============================================

export interface LayoutUIState {
  /** 
   * Parking session for the layout drawer.
   * - null: drawer closed
   * - object without id: start parking (create mode)
   * - object with id: parking detail (edit/view mode)
   */
  parkingSession: ParkingSession | null;
}

export interface LayoutUIActions {
  /** Set parking session to open drawer, null to close */
  setParkingSession: (session: ParkingSession | null) => void;
}

export type LayoutUIContextValue = LayoutUIState & LayoutUIActions;

// ============================================
// Context
// ============================================

const LayoutUIContext = createContext<LayoutUIContextValue | null>(null);

// ============================================
// Provider
// ============================================

interface LayoutUIProviderProps {
  children: React.ReactNode;
}

export const LayoutUIProvider: React.FC<LayoutUIProviderProps> = ({ children }) => {
  const [parkingSession, setParkingSessionState] = useState<ParkingSession | null>(null);

  const setParkingSession = useCallback((session: ParkingSession | null) => {
    setParkingSessionState(session);
  }, []);

  const value = useMemo<LayoutUIContextValue>(
    () => ({
      parkingSession,
      setParkingSession,
    }),
    [parkingSession, setParkingSession]
  );

  return (
    <LayoutUIContext.Provider value={value}>
      {children}
    </LayoutUIContext.Provider>
  );
};

// ============================================
// Hook
// ============================================

export const useLayoutUI = (): LayoutUIContextValue => {
  const context = useContext(LayoutUIContext);

  if (!context) {
    throw new Error("useLayoutUI must be used within a LayoutUIProvider");
  }

  return context;
};